import React, { createContext, useState, useEffect } from "react";
import socket from "../../../socket/socket";
import { axiosInstance } from "../../../api-handle/axiosInstance";
import { getDecodedToken, isAuthenticated } from "../../../api-handle/authService";

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);

    const fetchNotifications = async () => {
        // This provider wraps the WHOLE app, including public pages (/login,
        // /register). Never fire an authenticated call without a session — the
        // resulting 401 has nothing to refresh and just spams the console.
        if (!isAuthenticated()) return;
        try {
            const res = await axiosInstance.get("/mp/notifications");
            setNotifications(res.data || []);
        } catch (err) {
            console.error("Error fetching notifications", err);
        }
    };

    useEffect(() => {
        const handleNewNotification = (data) => {
            setNotifications((prev) => [data, ...prev]);
        };

        // Re-join the merchant room on every (re)connect — server-side room
        // membership does not survive a reconnect.
        const joinRoom = () => {
            const merchantId = getDecodedToken()?.merchant;
            if (merchantId) socket.emit("join", merchantId);
        };

        // Opens the socket (created with autoConnect:false so public pages stay
        // network-silent) and loads data — but ONLY with a session. Runs on
        // mount (page load while already logged in) and again on "auth:session"
        // (dispatched by authService.setSession after login), because login
        // navigates via react-router and never remounts this provider.
        const startSession = () => {
            if (!isAuthenticated()) return;
            if (!socket.connected) socket.connect();
            joinRoom(); // buffered by socket.io until the connection is up
            fetchNotifications();
        };

        socket.on("connect", joinRoom);
        socket.on("newNotification", handleNewNotification);
        startSession();
        window.addEventListener("auth:session", startSession);

        return () => {
            socket.off("connect", joinRoom);
            socket.off("newNotification", handleNewNotification);
            window.removeEventListener("auth:session", startSession);
        };
    }, []);

    const markAsRead = async (id) => {
        try {
            await axiosInstance.patch(`/mp/notifications/${id}/read`);
            setNotifications((prev) =>
                prev.map((item) =>
                    item._id === id ? { ...item, isRead: true } : item
                )
            );
        } catch (err) {
            console.error("Error marking as read", err);
        }
    };

    const markAllAsRead = async () => {
        try {
            await axiosInstance.patch(`/mp/notifications/mark-all-read`);
            setNotifications((prev) =>
                prev.map((item) => ({ ...item, isRead: true }))
            );
        } catch (err) {
            console.error("Error marking all as read", err);
        }
    };

    return (
        <NotificationContext.Provider
            value={{ notifications, setNotifications, markAsRead, markAllAsRead, fetchNotifications }}
        >
            {children}
        </NotificationContext.Provider>
    );
};